import React, { useEffect, useState } from 'react'

import { BodyText } from '@hm-group/fabric-components'
import { StatusMessage, Form, Label, SubmitButton } from '@pb/components'
import {
  getLabelFromStorage,
  isHmBrand,
  isNorthstarBrand,
  LoadingStates,
  trackEvent,
} from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import cn from 'classnames'
import { useSelector, useDispatch } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { getVoucherTracking, TRACKING_EVENTS } from 'App/constants'
import OPTIMIZELY from 'constants/optimizely'
import { CHECKOUT_TRACKING_EVENTS } from 'features/checkout/constants'
import { trackEvent as trackCOEvent } from 'features/checkout/duck/operations'
import { MESSAGE_CODE_TO_LABEL_KEY, VOUCHERS_LOCATION } from 'features/vouchers/constants'
import {
  createCartDiscountErrorTracking,
  operationPostVouchers,
} from 'features/vouchers/duck/operations'
import selectors from 'features/vouchers/duck/selectors'

import AddDiscountInput from '../AddDiscountInput'
import DiscountError from '../DiscountError'
import { trackAddVoucher } from '../helpers/tracking'
import { DiscountProps } from '../types'
import styles from './AddDiscountForm.scss'
import { DiscountFormError } from './types'

const Discounts: React.FC<DiscountProps> = ({ location, className }) => {
  const dispatch = useDispatch()

  const isCheckout = location === VOUCHERS_LOCATION.checkout
  const isCart = location === VOUCHERS_LOCATION.cart
  const isDecoupledCheckoutEnabled = useSelector(
    appSelectors.getIsDecoupledCheckoutEnabled || appSelectors.getIsAbTestVoucherModalEnabled,
  )
  const errors = useSelector(selectors.getErrors)

  const [{ enabled: isWOBannerActive }] = useDecision(OPTIMIZELY.LCA_WELCOME_OFFER_FEATURE)

  const isDiscountFormExtraSpacingEnabled =
    process.env.IS_DISCOUNT_FORM_EXTRA_SPACING_ENABLED === 'enabled'
  const postVouchersStatus = useSelector(selectors.getPostVouchersStatus)
  const isMaxVouchersReached = useSelector(selectors.getIsMaxVouchersReached)
  const isPostVouchersLoading =
    useSelector(selectors.getPostVouchersStatus) === LoadingStates.Loading
  const isVouchersLoading = useSelector(selectors.getGetVouchersStatus) === LoadingStates.Loading
  const copyKey =
    process.env.IS_DISCOUNT_FORM_ADDITIONAL_TEXT_ENABLED === 'enabled'
      ? 'checkout.discountcode.header'
      : 'checkout.discountcode.subheader'

  const shouldFormBeDisabled =
    isMaxVouchersReached || !!isPostVouchersLoading || !!isVouchersLoading

  const [formErrors, setFormErrors] = useState<DiscountFormError[]>([])
  const [shouldClearInput, setShouldClearInput] = useState(false)
  const [currentDiscountCode, setCurrentDiscountCode] = useState<string>('')

  useEffect(() => {
    if (postVouchersStatus === LoadingStates.LoadedSuccess) {
      setShouldClearInput(true)
    } else {
      setShouldClearInput(false)
    }
  }, [postVouchersStatus])

  useEffect(() => {
    const discountErrors = errors.filter(
      (error) => error.target === 'couponCode' && !error.isTechnicalError,
    )
    if (discountErrors.length > 0) {
      const { code, type } = discountErrors[0]
      const errorMessageLabelKey = MESSAGE_CODE_TO_LABEL_KEY[code]
      const errorMessage = errorMessageLabelKey
        ? getLabelFromStorage(errorMessageLabelKey)
        : 'discount.code.not.valid'

      setFormErrors([{ type, target: 'discountCode', message: errorMessage }])
    }
  }, [errors])

  const addDiscountCode = ({ discountCode }: { discountCode: string }): void => {
    if (!discountCode) return

    if (isWOBannerActive) {
      trackAddVoucher(discountCode.trim())
    }
    if (isCheckout) {
      dispatch(
        trackCOEvent({
          event: CHECKOUT_TRACKING_EVENTS.addDiscountCode.id,
          add: {
            discount_code: discountCode,
            dep_variable_name: ['discount_code'],
            dep_variable_format: ['string'],
          },
        }),
      )
    }

    dispatch(operationPostVouchers({ voucherCode: discountCode.trim() }, location, true))
  }

  const onClickSubmit = (): void => {
    if (isCart && !currentDiscountCode) {
      trackEvent({
        ...TRACKING_EVENTS.invalidDiscount.data,
        ...createCartDiscountErrorTracking(),
        ...getVoucherTracking(''),
        ...{ error_message: 'Empty discount code' },
        ...(!(isHmBrand || isNorthstarBrand) && { discount_id: '' }),
      })
    }
  }

  return (
    <Form
      currentData={{ discountCode: '' }}
      onSubmit={addDiscountCode}
      errors={formErrors}
      shouldResetForm={shouldClearInput}
      className={cn(
        styles.container,
        !isDecoupledCheckoutEnabled && styles.containerOldVersion,
        isDiscountFormExtraSpacingEnabled && styles.reduceExtraSpacing,
        className,
      )}
    >
      {isMaxVouchersReached && (
        <StatusMessage
          className={styles.maxReachedMessage}
          icon="info"
          type="info"
          message={getLabelFromStorage('checkout.discounts.max.reached')}
        />
      )}
      <DiscountError className={styles.error} />
      <BodyText
        Tag="label"
        className={cn(styles.label, !isDecoupledCheckoutEnabled && styles.labelOldVersion)}
        htmlFor="discountCode"
      >
        {getLabelFromStorage(copyKey)}
      </BodyText>

      <AddDiscountInput
        error={formErrors}
        isExtraInnerInputPaddingEnabled={isDiscountFormExtraSpacingEnabled}
        isAdditionalTextLarge={process.env.IS_DISCOUNT_FORM_LARGE_TEXT_ENABLED === 'enabled'}
        className={styles.input}
        disabled={shouldFormBeDisabled}
        location={location}
        onChangeDiscount={(val) => setCurrentDiscountCode(val)}
      />
      <SubmitButton
        onClick={onClickSubmit}
        data-testid="addDiscountSubmit"
        className={cn(
          styles.button,
          isDiscountFormExtraSpacingEnabled && styles.reduceExtraSpacing,
        )}
        disabled={shouldFormBeDisabled}
        secondary={
          !isDecoupledCheckoutEnabled || process.env.IS_VOUCHER_MODAL_ENABLED === 'disabled'
        }
        aria-label={getLabelFromStorage('checkout.discountcode.addbutton')?.toLowerCase()}
      >
        <Label aria-hidden="true" copyKey="checkout.discountcode.addbutton" />
      </SubmitButton>
    </Form>
  )
}

export default Discounts
